import { motion } from "framer-motion";
import heroImg from "@/assets/hero-massage-opt.webp";
import { useLocalizedContent } from "@/hooks/use-localized-content";
import { useTranslation } from "react-i18next";
import { Star, Shield, Clock } from "lucide-react";

const Hero = () => {
  const { c, content } = useLocalizedContent();
  const { t } = useTranslation();

  const badge = c("hero_badge", "hero.badge");
  const title = c("hero_title", "hero.title");
  const subtitle = c("hero_subtitle", "hero.subtitle");
  const image = content.hero_image || heroImg;
  const ctaPrimary = c("hero_cta_primary", "hero.ctaPrimary");
  const ctaSecondary = c("hero_cta_secondary", "hero.ctaSecondary");
  const rating = content.hero_rating || "4.9/5";
  const clientsCount = c("hero_clients_count", "hero.clientsCount");

  const words = title.split(" ");
  const highlight = words.slice(-3).join(" ");
  const beforeHighlight = words.slice(0, -3).join(" ");

  return (
    <section id="inicio" className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img src={image} alt="Mente Sã massagem relaxante ao domicílio Lisboa aromaterapia óleos essenciais bem-estar emocional Oeiras Cascais Paço de Arcos spa domicílio Portugal 2026" className="w-full h-full object-cover" width={1920} height={1080} fetchPriority="high" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
      </div>

      <div className="container mx-auto px-6 relative z-10 pt-24 pb-16">
        <div className="max-w-2xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <motion.span
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1, duration: 0.3 }}
              className="inline-flex items-center gap-2 rounded-full bg-primary/15 backdrop-blur-sm border border-primary/20 px-5 py-2 text-xs font-semibold text-primary mb-8"
            >
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              {badge}
            </motion.span>

            <h1 className="font-display text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6 text-foreground">
              {beforeHighlight}{" "}
              <span className="text-gradient-wine">{highlight}</span>
            </h1>

            <p className="font-body text-base sm:text-lg md:text-xl text-muted-foreground max-w-lg mb-8 sm:mb-10 leading-relaxed">
              {subtitle}
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 mb-10 sm:mb-12">
              <motion.a
                href="#agendar"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 text-base font-semibold text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all w-full sm:w-auto"
              >
                {ctaPrimary}
              </motion.a>
              <a
                href="#servicos"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-primary/30 backdrop-blur-sm px-8 py-4 text-base font-semibold text-primary hover:bg-primary/10 transition-colors w-full sm:w-auto"
              >
                {ctaSecondary}
              </a>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.3 }}
              className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6 text-sm"
            >
              <div className="flex items-center gap-2 text-muted-foreground">
                <div className="flex -space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={14} className="fill-primary text-primary" />
                  ))}
                </div>
                <span className="font-medium">{rating}</span>
                <span className="text-muted-foreground/70">• {clientsCount}</span>
              </div>
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Shield size={14} className="text-primary" />
                <span>{t("hero.certified")}</span>
              </div>
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Clock size={14} className="text-primary" />
                <span>{t("hero.response")}</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
